export type OperationalAllocation = {
  id: string;
  type: string;
  status: string;
  startDate: string;
  endDate: string;
  bookingId: string | null;
  bookingNumber: string | null;
  workshopAppointmentId: string | null;
};

export type OperationalCar = {
  id: string;
  status: string;
  readyToHandover: boolean;
};

export type CarOperationalAction = {
  label:
    | 'toBooking'
    | 'toReturn'
    | 'toHandover'
    | 'toTransfer'
    | 'toPartnerRent'
    | 'toPreparation'
    | 'toWorkshop'
    | 'toNextHandover'
    | 'toNextBooking'
    | 'updateCarStatus';
  to: { name: string; params: Record<string, string> };
};

export type CarOperationalState = {
  id: string;
  tone: 'default' | 'warning' | 'success';
  label: 'activeRental' | 'handoverPending' | 'notPrepared' | 'inWorkshop' | 'readyForHandover';
  bookingId?: string;
  bookingNumber?: string;
  date?: string;
  endDate?: string;
  returnDate?: string;
  actions: CarOperationalAction[];
};

const RENTAL_TYPES = new Set([
  'BOOKING',
  'TRANSFER_DELIVERY',
  'TRANSFER_RETURN',
  'PARTNER_BOOKING',
]);

const BOOKING_TYPES = new Set(['BOOKING', 'PARTNER_BOOKING']);

const hasOverlapNow = (startDate: string, endDate: string, now: Date) =>
  new Date(startDate) <= now && new Date(endDate) >= now;

const getRentalActions = (
  allocation: OperationalAllocation,
  mode: 'active' | 'pending',
): CarOperationalAction[] => {
  if (allocation.type === 'BOOKING' && allocation.bookingId) {
    if (mode === 'active') {
      return [
        {
          label: 'toBooking',
          to: { name: 'bookingEdit', params: { id: allocation.bookingId } },
        },
        {
          label: 'toReturn',
          to: { name: 'returnOverview', params: { id: allocation.bookingId } },
        },
      ];
    }
    return [
      {
        label: 'toHandover',
        to: { name: 'handoverDetail', params: { id: allocation.bookingId } },
      },
    ];
  }
  if (allocation.type === 'PARTNER_BOOKING') {
    return [
      {
        label: 'toPartnerRent',
        to: { name: 'partnerRentHandover', params: { id: allocation.id } },
      },
    ];
  }
  if (allocation.type === 'TRANSFER_DELIVERY' || allocation.type === 'TRANSFER_RETURN') {
    return [
      {
        label: 'toTransfer',
        to: { name: 'transferDetail', params: { id: allocation.id } },
      },
    ];
  }
  return [];
};

const getNextBookingActions = (
  carId: string,
  nextAllocation: OperationalAllocation | undefined,
  upcomingBooking: OperationalAllocation,
): CarOperationalAction[] => {
  const updateStatus: CarOperationalAction = {
    label: 'updateCarStatus',
    to: { name: 'editCarStatus', params: { id: carId } },
  };
  if (!nextAllocation || nextAllocation.id !== upcomingBooking.id) return [updateStatus];
  if (upcomingBooking.type === 'BOOKING' && upcomingBooking.bookingId) {
    return [
      {
        label: 'toNextHandover',
        to: { name: 'handoverDetail', params: { id: upcomingBooking.bookingId } },
      },
      {
        label: 'toNextBooking',
        to: { name: 'bookingEdit', params: { id: upcomingBooking.bookingId } },
      },
      updateStatus,
    ];
  }
  if (upcomingBooking.type === 'PARTNER_BOOKING') {
    return [
      {
        label: 'toNextHandover',
        to: { name: 'partnerRentHandover', params: { id: upcomingBooking.id } },
      },
      updateStatus,
    ];
  }
  return [updateStatus];
};

const getLastReturnEndDate = (allocations: OperationalAllocation[]) =>
  allocations
    .filter((allocation) => BOOKING_TYPES.has(allocation.type) && allocation.status === 'RETURNED')
    .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())[0]?.endDate;

export const getCarOperationalStates = ({
  car,
  allocations,
  now,
}: {
  car: OperationalCar;
  allocations: OperationalAllocation[];
  now: Date;
}): CarOperationalState[] => {
  const rentals = allocations.filter((allocation) => RENTAL_TYPES.has(allocation.type));
  const pending = rentals.filter(
    (allocation) =>
      allocation.status === 'CONFIRMED' &&
      hasOverlapNow(allocation.startDate, allocation.endDate, now),
  );
  const active = rentals.filter((allocation) => allocation.status === 'HANDED_OVER');
  const upcoming = allocations
    .filter(
      (allocation) =>
        new Date(allocation.startDate) > now &&
        (allocation.status === 'CONFIRMED' || allocation.status === 'NOT_CONFIRMED'),
    )
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  const upcomingBooking = upcoming.find((allocation) => BOOKING_TYPES.has(allocation.type));
  const needsPreparation = car.status === 'FLOAT_IN' && !car.readyToHandover;

  const states: CarOperationalState[] = [
    ...pending.map((allocation) => ({
      id: `handoverPending-${allocation.id}`,
      tone: 'warning' as const,
      label: 'handoverPending' as const,
      bookingId: allocation.bookingId ?? undefined,
      bookingNumber: allocation.bookingNumber ?? undefined,
      date: allocation.startDate,
      actions: getRentalActions(allocation, 'pending'),
    })),
    ...active.map((allocation) => ({
      id: `activeRental-${allocation.id}`,
      tone: 'default' as const,
      label: 'activeRental' as const,
      bookingId: allocation.bookingId ?? undefined,
      bookingNumber: allocation.bookingNumber ?? undefined,
      date: allocation.startDate,
      endDate: allocation.endDate,
      actions: getRentalActions(allocation, 'active'),
    })),
    ...allocations
      .filter(
        (allocation) =>
          allocation.type === 'WORKSHOP_APPOINTMENT' &&
          allocation.status === 'HANDED_OVER' &&
          allocation.workshopAppointmentId &&
          hasOverlapNow(allocation.startDate, allocation.endDate, now),
      )
      .map((allocation) => ({
        id: `inWorkshop-${allocation.workshopAppointmentId}`,
        tone: 'warning' as const,
        label: 'inWorkshop' as const,
        actions: [
          {
            label: 'toWorkshop' as const,
            to: {
              name: 'editWorkshopAppointment',
              params: { id: allocation.workshopAppointmentId! },
            },
          },
        ],
      })),
  ];

  if (needsPreparation) {
    states.push({
      id: 'notPrepared',
      tone: 'warning',
      label: 'notPrepared',
      returnDate: getLastReturnEndDate(allocations),
      actions: [
        {
          label: 'toPreparation',
          to: { name: 'editCarStatus', params: { id: car.id } },
        },
      ],
    });
  }

  if (
    car.status === 'FLOAT_IN' &&
    !pending.length &&
    !active.length &&
    !needsPreparation &&
    upcomingBooking
  ) {
    states.push({
      id: 'readyForHandover',
      tone: 'success',
      label: 'readyForHandover',
      bookingId: upcomingBooking.bookingId ?? undefined,
      bookingNumber: upcomingBooking.bookingNumber ?? undefined,
      date: upcomingBooking.startDate,
      actions: getNextBookingActions(car.id, upcoming[0], upcomingBooking),
    });
  }

  return states.filter((state) => state.actions.length > 0);
};
